import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Booking, CreateBookingForm, Route } from '../types';

const bookingReference = () => {
  const date = new Date();
  const stamp = date.toISOString().slice(2, 10).replace(/-/g, '');
  const random = Math.random().toString(36).slice(2, 7).toUpperCase();
  return `WIC-${stamp}-${random}`;
};

const mapRoute = (id: string, data: any): Route => ({
  id,
  fromCity: data.fromCity || '',
  toCity: data.toCity || '',
  departureTime: data.departureTime || '',
  arrivalTime: data.arrivalTime || '',
  price: data.price || 0,
  distance: data.distance || 0,
  duration: data.duration || 0,
  schedule: data.schedule || { isRecurring: false, days: [] },
  stops: data.stops || [],
  rates: data.rates || { luggage: 0, bag: 0 },
  isActive: data.isActive ?? true,
  createdAt: data.createdAt?.toDate?.() || new Date(),
  updatedAt: data.updatedAt?.toDate?.() || new Date(),
});

const mapBooking = async (id: string, data: any): Promise<Booking> => {
  let route: Route | undefined;

  if (data.routeId) {
    const routeSnap = await getDoc(doc(db, 'routes', data.routeId));
    if (routeSnap.exists()) {
      route = mapRoute(routeSnap.id, routeSnap.data());
    }
  }

  return {
    id,
    userId: data.userId,
    routeId: data.routeId,
    passengerName: data.passengerName || '',
    passengerPhone: data.passengerPhone || '',
    passengerEmail: data.passengerEmail || '',
    seatNumber: data.seatNumber || 'Auto assign',
    bookingDate: data.bookingDate?.toDate?.() || new Date(),
    travelDate: data.travelDate?.toDate?.() || new Date(),
    totalAmount: data.totalAmount || 0,
    bookingStatus: data.bookingStatus || data.status || 'pending',
    paymentStatus: data.paymentStatus || 'pending',
    bookingReference: data.bookingReference || id,
    notes: data.notes,
    createdAt: data.createdAt?.toDate?.() || new Date(),
    updatedAt: data.updatedAt?.toDate?.() || new Date(),
    route,
  };
};

export class ClientBookingService {
  static async getBookingsForUser(userId: string): Promise<Booking[]> {
    const bookingsRef = collection(db, 'bookings');
    const q = query(
      bookingsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);

    return Promise.all(snapshot.docs.map((bookingDoc) => mapBooking(bookingDoc.id, bookingDoc.data())));
  }

  static async createBooking(data: CreateBookingForm): Promise<Booking> {
    const now = Timestamp.now();
    const newBooking = {
      ...data,
      bookingDate: now,
      travelDate: Timestamp.fromDate(data.travelDate),
      bookingStatus: 'pending',
      paymentStatus: 'pending',
      bookingReference: bookingReference(),
      createdAt: now,
      updatedAt: now,
    };

    const bookingDoc = await addDoc(collection(db, 'bookings'), newBooking);
    return mapBooking(bookingDoc.id, newBooking);
  }
}
